// Every driver, split at the approval latch: Applicants, then Drivers.
//
// The latch is the only split that holds. Before a driver has ever been approved
// the application funnel owns their status; afterwards driver standing does, and
// standing mirrors *down* onto the funnel — INVALID_INFORMATION writes
// `awaiting_docs`, the same value an applicant who never finished carries. So one
// combined list would show a working driver whose insurance lapsed as
// indistinguishable from someone who never got started.
//
// Each tab's filters are that phase's states: the five the driver's own app shows on
// one side, the five standings on the other. The applicant five are collapsed
// server-side from the stored funnel, so statuses from app releases predating the enum
// still land somewhere — /drivers-approval remains the surface for the raw column.
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Paper, Stack, Typography, CircularProgress, Button, Pagination, TintedStatusPill } from '../../ui/core';
import { flushPageSx, hairline, softFill, scrollY, surfaceCardSx } from '../../ui/styles';
import DashboardLayout from '../../layouts/DashoardLayout';
import api from '../../lib/api';
import useQuery from '../../hooks/useQuery';
import { patchSearch } from '../../helpers/utils';
import { APPLICATION_STATUS_LABELS, DOC_LABELS, STANDING_LABELS, phaseStatus } from '../DriverProfile/model';
import { CountBadge, FilterMenu, ItemStatusChip, SegmentedControl, StatusDot } from '../DriverProfile/components';
import { ChevronRightIcon } from '../../ui/icons';

const PAGE = 25;

/**
 * Hours since something, as a phrase — an upload on a queue row, a status or standing
 * change on a person row. Takes hours because the server already did the subtraction
 * and sends `ageHours`: re-deriving it from a timestamp against the browser clock
 * would disagree with the `olderThanHours` filter that selected the row, and would
 * make two tabs showing the same driver disagree with each other on a stale tab.
 */
const age = (hours) => {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
};

/** At most this many document names on a queue row before the rest become "+n more". */
const NAMED_DOCS = 3;

/**
 * One row of the review queue. A DRIVER, with everything of theirs that is waiting —
 * still a different object from the two tabs beside it, which take a driver-facing
 * status ("Under review" is equally true of a licence a human must decide and a
 * background check running at Checkr).
 *
 * Grouped rather than one row per document because that is how the work is done: a
 * reviewer opens the record once and settles all of it, and three rows for one person
 * were three passes over the same page — and a 1,200-deep queue that is really 400
 * people reads as unworkable.
 */
const QueueRow = ({ row }) => {
  // An API that has not shipped the grouping yet returns the document's own fields on
  // the row, which is exactly a driver with one document — so it reads as one rather
  // than as a driver whose every document is "Missing". Deletable once hitch-services
  // #3427 is out everywhere.
  const docs = row.documents || [row];
  // Named per document, not per type: two insurance documents on two vehicles are two
  // separate decisions, so the vehicle is what tells them apart.
  const names = docs.map(
    (d) => `${DOC_LABELS[d.documentType] || d.documentType}${d.vehicleLabel ? ` · ${d.vehicleLabel}` : ''}`,
  );
  const shown = names.slice(0, NAMED_DOCS);
  const rest = names.length - shown.length;

  return (
    <>
      {/* The oldest document's status. The tab asks for one status at a time, so a
          row's documents agree — this is the dot for the row, not a claim that the
          rest match it. */}
      <StatusDot value={docs[0]?.status} />
      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {row.driverName || 'Unknown driver'}
      </Typography>
      <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', flex: 1, minWidth: 0 }}>
        {shown.join(' · ')}
        {rest > 0 ? ` · +${rest} more` : ''}
      </Typography>
      {/* How many decisions this row is, which is what makes one queue row worth more
          than another at the same age. Silent at one — a "1" on most rows is noise. */}
      {docs.length > 1 && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {docs.length} documents
        </Typography>
      )}
      <ItemStatusChip value={docs[0]?.status} />
      {/* Their OLDEST document's age, red past a day: age is the thing nobody sees
          until a driver complains. */}
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: row.ageHours >= 24 ? 700 : 400,
          color: row.ageHours >= 24 ? 'error.main' : 'text.secondary',
          whiteSpace: 'nowrap',
          minWidth: 56,
          textAlign: 'right',
        }}
      >
        {age(row.ageHours)}
      </Typography>
    </>
  );
};

/**
 * One row of either driver list. The pill is the same one their profile shows.
 *
 * The age is how long they have held that state — an applicant's last funnel move,
 * a driver's last standing change. Same `ageHours` the queue sends, and same phrase,
 * because these three tabs are read together and a state with no duration is the
 * thing you cannot triage: "twelve under review" says nothing until you know whether
 * that is twelve people from this morning or one from March.
 *
 * NOT red past a day, which is the one thing it does differently from the queue.
 * There the age is an SLA — something is waiting on us — and here it is context: a
 * driver cleared for 300 days is the healthy case, and colouring it as a breach
 * would make the whole Drivers tab an alarm.
 */
const PersonRow = ({ row, hasBeenApproved }) => {
  const head = phaseStatus({
    hasBeenApproved,
    currentStanding: row.standing,
    applicationStatus: row.applicationStatus,
  });
  return (
    <>
      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, flex: 1, minWidth: 0 }}>
        {row.name || 'Unknown driver'}
      </Typography>
      <TintedStatusPill palette={head.palette} label={head.label} />
      {/* Blank, not "just now", when the server sends null: a driver approved before
          the standing trail existed has no first event, and dating them to now would
          put the entire back catalogue at the top of an age-sorted read. */}
      <Typography
        sx={{
          fontSize: '12px',
          color: 'text.disabled',
          whiteSpace: 'nowrap',
          minWidth: 56,
          textAlign: 'right',
        }}
      >
        {row.ageHours == null ? '' : age(row.ageHours)}
      </Typography>
    </>
  );
};

/**
 * The tabs. Two are populations of PEOPLE split at the approval latch; the third is a
 * queue of the same people, cut by what is waiting on US rather than by status — the
 * way Linear puts Triage beside Issues rather than inside them.
 *
 * Each phase owns its loader, its filter set, the id it links on, and its row. Adding
 * the queue therefore added no branches to the shell below — the one thing it does
 * differently is which id it navigates by, since a queue row carries its driver's id
 * under another name.
 *
 * `approved` is deliberately absent from the applicant filters: a driver whose status
 * is approved is by definition past the latch, so that list would always be empty.
 * Anything that does turn up there — a status set without a history entry — still
 * appears under "Everyone".
 */
const PHASES = [
  {
    key: 'applicants',
    title: 'Applicants',
    empty: 'No applicant is in this state.',
    load: ({ filter, ...page }) => api.driverProfile.getApplicants({ status: filter, ...page }),
    filters: [
      { key: '', label: 'Everyone' },
      ...['in_progress', 'in_review', 'update_application', 'rejected'].map((key) => ({
        key,
        label: APPLICATION_STATUS_LABELS[key],
      })),
    ],
    idOf: (row) => row.mongoId,
    row: (row) => <PersonRow row={row} hasBeenApproved={false} />,
  },
  {
    key: 'drivers',
    title: 'Drivers',
    empty: 'No driver is in this standing.',
    load: ({ filter, ...page }) => api.driverProfile.getDriversByStanding({ standing: filter, ...page }),
    filters: [
      { key: '', label: 'Everyone' },
      ...['CLEARED', 'UNDER_REVIEW', 'INVALID_INFORMATION', 'SUSPENDED', 'DEACTIVATED'].map((key) => ({
        key,
        label: STANDING_LABELS[key],
      })),
    ],
    idOf: (row) => row.mongoId,
    row: (row) => <PersonRow row={row} hasBeenApproved />,
  },
  {
    key: 'review',
    title: 'Needs review',
    empty: 'Nothing is waiting on a person.',
    // NEEDS_REVIEW only, though the endpoint defaults to PENDING as well. PENDING
    // means nothing has looked at the document yet and does not clear itself unless
    // the driver-doc-pipeline flag is applying decisions — a backlog, not a queue.
    // Sorted together by age, backfilled 20-day PENDING rows outrank a 1h escalation
    // while being the less actionable of the two.
    load: ({ filter, ...page }) =>
      api.driverProfile.getReviewQueue({
        status: ['NEEDS_REVIEW'],
        olderThanHours: filter ? Number(filter) : undefined,
        ...page,
      }),
    // Age, because age is the only axis a queue is really triaged on. These narrow
    // rather than re-sort — the list is oldest-first either way.
    filters: [
      { key: '', label: 'Any age' },
      { key: '4', label: 'Over 4 hours' },
      { key: '24', label: 'Over a day' },
      { key: '72', label: 'Over three days' },
    ],
    // A queue row's driver id arrives under a different name from the other two lists.
    idOf: (row) => row.driverMongoId,
    // Lands on the driver's own review filter rather than their whole record.
    search: '?mode=review',
    row: (row) => <QueueRow row={row} />,
  },
];

const DriversShell = () => {
  const history = useHistory();
  const location = useLocation();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Every filter's count, both phases, from one request on mount. Failure is
  // swallowed: the badges are an aid to picking a filter, and losing them should not
  // cost a reviewer the list itself.
  const [counts, setCounts] = useState(null);
  // The queue's size — drivers waiting, not documents — for its tab badge. Its own
  // request because /counts counts by status, and `limit: 1` because only the total
  // is used.
  // NEEDS_REVIEW only, matching what the tab shows — a badge counting a backlog nobody
  // is expected to work is how a call to action becomes background noise.
  const [queued, setQueued] = useState(null);

  useEffect(() => {
    api.driverProfile.getCounts().then(setCounts).catch(() => setCounts(null));
    api.driverProfile
      .getReviewQueue({ status: ['NEEDS_REVIEW'], limit: 1 })
      .then((r) => setQueued(r.total))
      .catch(() => setQueued(null));
  }, []);

  // Phase, filter and page live in the URL so any cut of this is a link — "the
  // applicants still waiting on documents" is shareable, not instructions.
  const query = useQuery();
  const phase = PHASES.find((p) => p.key === query.phase) || PHASES[0];
  // Validated against THIS phase's options, so a filter left over from the other
  // tab in a hand-edited URL falls back to everyone rather than 400ing.
  const filter = phase.filters.some((f) => f.key && f.key === query.filter) ? query.filter : '';
  // Guarded, because the page number comes from a URL a person can edit, and NaN
  // would become an offset of NaN.
  const page = Math.max(parseInt(query.page, 10) || 1, 1);

  // Built here rather than in PHASES because the count is data. Absent until the
  // counts arrive, at which point CountBadge starts rendering — see CountBadge for
  // why absent and zero are not the same thing.
  const phaseTabs = PHASES.map((p) => ({
    key: p.key,
    label: (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} component="span">
        <span>{p.title}</span>
        {/* The queue's total comes from its own request; the other two from /counts. */}
        <CountBadge count={p.key === 'review' ? queued : counts?.[p.key]?.total} />
      </Stack>
    ),
  }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await phase.load({ filter: filter || undefined, limit: PAGE, offset: (page - 1) * PAGE }));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not load these drivers');
    } finally {
      setLoading(false);
    }
  }, [phase, filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setSearch = (patch, { replace = false } = {}) => {
    const search = patchSearch(location.search, patch);
    (replace ? history.replace : history.push)({ search });
  };

  // Resets the page: changing a filter while on page 4 would otherwise ask for an
  // offset the new result set may not reach, and show an empty page. Replaced
  // rather than pushed — narrowing a filter three times should not need three
  // presses of back to leave.
  const changeFilter = (next) => setSearch({ filter: next || null, page: null }, { replace: true });
  // Pushed, and clears the filter, which belongs to the tab being left.
  const changePhase = (next) => setSearch({ phase: next === PHASES[0].key ? null : next, filter: null, page: null });
  // Pushed, so back returns to the previous page of results.
  const changePage = (next) => setSearch({ page: next === 1 ? null : next });

  return (
    // Shrinkable flex child; the height ceiling is flushPageSx's, not this one's.
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.75, borderBottom: (t) => `1px solid ${hairline(t)}`, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
            Drivers
          </Typography>
          {/* Each tab's total rides on the tab itself, so the size of each queue is
              legible without switching to it. */}
          <SegmentedControl value={phase.key} onChange={changePhase} options={phaseTabs} />
          <Box sx={{ flex: 1 }} />
          <FilterMenu
            value={filter}
            onChange={changeFilter}
            options={phase.filters}
            // The queue's filters are age buckets, which /counts knows nothing about —
            // undefined so CountBadge renders nothing rather than a misleading zero.
            counts={
              phase.key === 'review'
                ? undefined
                : { ...(counts?.[phase.key]?.byState || {}), '': counts?.[phase.key]?.total }
            }
          />
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, ...scrollY }}>
        <Box sx={{ maxWidth: 980, mx: 'auto', px: 3, py: 2.5 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={22} />
            </Stack>
          ) : error ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
              <Typography sx={{ fontSize: '14px' }}>{error}</Typography>
              <Button variant="outlined" onClick={load} sx={{ textTransform: 'none' }}>
                Try again
              </Button>
            </Stack>
          ) : !data.rows.length ? (
            <Box sx={{ border: (t) => `1px dashed ${hairline(t)}`, borderRadius: '8px', px: 2, py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{phase.empty}</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ border: (t) => `1px solid ${hairline(t)}`, borderRadius: '8px', overflow: 'hidden' }}>
                {data.rows.map((row, i) => {
                  // Which phase a row belongs to is settled by which list produced it,
                  // so the phase supplies both the id to link on and the row itself.
                  const id = phase.idOf(row);
                  return (
                    <Stack
                      key={row.id || id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      onClick={() => history.push(`/driver-profile/${id}${phase.search || ''}`)}
                      sx={{
                        px: 1.75,
                        py: 1.2,
                        cursor: 'pointer',
                        borderTop: i === 0 ? 'none' : (t) => `1px solid ${hairline(t)}`,
                        '&:hover': { backgroundColor: (t) => softFill(t) },
                      }}
                    >
                      {phase.row(row)}
                      <ChevronRightIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                    </Stack>
                  );
                })}
              </Box>

              {data.total > PAGE && (
                <Stack alignItems="center" sx={{ mt: 2.5 }}>
                  <Pagination
                    count={Math.ceil(data.total / PAGE)}
                    page={page}
                    onChange={(_, next) => changePage(next)}
                    size="small"
                  />
                </Stack>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Drivers = () => (
  <DashboardLayout flush title="Drivers">
    <Box sx={flushPageSx}>
      <Paper elevation={0} sx={{ ...surfaceCardSx, flex: 1, minWidth: 0 }}>
        <DriversShell />
      </Paper>
    </Box>
  </DashboardLayout>
);

export default Drivers;
